import { getLinkInfo, isSafeUrl } from 'lib/getLinkInfo';
import React from 'react';
import { NavLink } from 'react-router-dom';

interface LinkWrapperProps {
    link?: string;
    target?: React.HTMLAttributeAnchorTarget;
    className?: string;
    title?: string;
    'aria-label'?: string;
}

/**
 * Handles internal and external links, using an <a> or <NavLink> as appropriate
 * Open Access URLs will use an external link automatically
 * Unstyled by default but supports a className property
 */
export const LinkWrapper: React.FC<LinkWrapperProps> = ({
    link,
    className,
    children,
    target = '_blank',
    title,
    'aria-label': ariaLabel
}) => {
    // link is frequently sourced from data-driven or user/AI-authored content (drilldown fields,
    // markdown, plugin config) — reject unsafe schemes (javascript:, data:, etc.)
    const safeLink = isSafeUrl(link) ? link : undefined;
    const linkInfo = getLinkInfo(safeLink || '');

    if (safeLink && (linkInfo.isExternal || linkInfo.isOpenAccessUrl)) {
        return (
            <a
                href={safeLink}
                rel='noopener noreferrer'
                className={className}
                target={target}
                title={title}
                aria-label={ariaLabel}
            >
                {children}
            </a>
        );
    }

    if (safeLink && !linkInfo.isExternal) {
        return (
            <NavLink
                to={linkInfo.relativeURL}
                target={target}
                className={className}
                title={title}
                aria-label={ariaLabel}
            >
                {children}
            </NavLink>
        );
    }

    return <>{children}</>;
};
